import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import GuestOnboardingModal from "components/shared/GuestOnboardingModal";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import useDebounce from "utils/useDebounce";
import getGuestDisplayName from "utils/getGuestDisplayName";
import { Search, Trash2 } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import { AdminTable } from "components/shared/AdminTable";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { SortableTableHead } from "components/shared/SortableTableHead";

const GUEST_SEARCH_RESULT_LIMIT = 50;

type SortField =
  | "name"
  | "email"
  | "guestTypes"
  | "validFrom"
  | "validTo"
  | "deleted";
type SortDirection = "asc" | "desc";

export default function AdminGuests({
  search,
  setSearch,
}: {
  search: string;
  setSearch: (search: string) => void;
}) {
  const debouncedSearchTerm = useDebounce(search, 500);
  const { data, isLoading, isError } = useGetGuests(debouncedSearchTerm);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [onboardModalOpen, setOnboardModalOpen] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data || !sortField) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case "name":
          comparison = getGuestDisplayName(a.firstName, a.lastName).localeCompare(
            getGuestDisplayName(b.firstName, b.lastName),
          );
          break;
        case "email":
          comparison = (a.email ?? "").localeCompare(b.email ?? "");
          break;
        case "guestTypes":
          comparison = a.guestTypes.join(", ").localeCompare(b.guestTypes.join(", "));
          break;
        case "validFrom":
          comparison = (a.validFrom ?? "").localeCompare(b.validFrom ?? "");
          break;
        case "validTo":
          comparison = (a.validTo ?? "").localeCompare(b.validTo ?? "");
          break;
        case "deleted":
          comparison = Number(!!a.deletedTimestamp) - Number(!!b.deletedTimestamp);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [data, sortField, sortDirection]);

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Guests
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Search for a guest by name, email, or ID.
          </p>
        </div>
        <Button className="mt-4 sm:mt-0" onClick={() => setOnboardModalOpen(true)} type="button">
          Onboard Guest
        </Button>
      </header>

      <AdminTable
        aria-labelledby="guests-list-heading"
        toolbar={
          <>
            <div className="flex flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <h2 id="guests-list-heading" className="text-base font-semibold text-slate-900">
                Guests
              </h2>
              <label className="relative block w-full sm:w-80">
                <span className="sr-only">Search guests</span>
                <Search
                  aria-hidden="true"
                  className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
                />
                <input
                  className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, or ID"
                />
              </label>
            </div>
            {data && data.length === GUEST_SEARCH_RESULT_LIMIT ? (
              <div className="px-4 pb-3 text-xs text-amber-700">
                Showing the first {GUEST_SEARCH_RESULT_LIMIT} results. Refine your
                search to narrow down the list.
              </div>
            ) : null}
          </>
        }
      >
        {isError ? (
          <tbody>
            <tr>
              <td colSpan={6}>
                <Alert variant="destructive">There was an error loading guests.</Alert>
              </td>
            </tr>
          </tbody>
        ) : search.trim().length === 0 ? (
          <tbody>
            <tr>
              <td colSpan={6}>
                <div className="flex items-center justify-center gap-3 text-sm min-h-80 text-slate-500">
                  No guests found. Search to get results.
                </div>
              </td>
            </tr>
          </tbody>
        ) : isLoading ? (
          <tbody>
            <tr>
              <td colSpan={6}>
                <div className="flex items-center justify-center gap-3 text-sm min-h-80 text-slate-500">
                  Loading...
                </div>
              </td>
            </tr>
          </tbody>
        ) : sortedData ? (
          <>
            <thead>
              <tr>
                <SortableTableHead
                  active={sortField === "name"}
                  ariaLabel="Sort by name"
                  direction={sortField === "name" ? sortDirection : "asc"}
                  onSort={() => handleSort("name")}
                >
                  Name
                </SortableTableHead>
                <SortableTableHead
                  active={sortField === "email"}
                  ariaLabel="Sort by email"
                  direction={sortField === "email" ? sortDirection : "asc"}
                  onSort={() => handleSort("email")}
                >
                  Email
                </SortableTableHead>
                <SortableTableHead
                  active={sortField === "guestTypes"}
                  ariaLabel="Sort by guest types"
                  direction={sortField === "guestTypes" ? sortDirection : "asc"}
                  onSort={() => handleSort("guestTypes")}
                >
                  Guest Types
                </SortableTableHead>
                <SortableTableHead
                  active={sortField === "validFrom"}
                  ariaLabel="Sort by valid from"
                  direction={sortField === "validFrom" ? sortDirection : "asc"}
                  onSort={() => handleSort("validFrom")}
                >
                  Valid From
                </SortableTableHead>
                <SortableTableHead
                  active={sortField === "validTo"}
                  ariaLabel="Sort by valid to"
                  direction={sortField === "validTo" ? sortDirection : "asc"}
                  onSort={() => handleSort("validTo")}
                >
                  Valid To
                </SortableTableHead>
                <SortableTableHead
                  active={sortField === "deleted"}
                  ariaLabel="Sort by deleted status"
                  className="text-center"
                  direction={sortField === "deleted" ? sortDirection : "asc"}
                  onSort={() => handleSort("deleted")}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                </SortableTableHead>
              </tr>
            </thead>
            <tbody>
                {sortedData.map((guest) => (
                  <tr key={guest.id}>
                    <td>
                      <div>
                        <Link
                          to={`/admin/guests/${guest.id}`}
                          state={{ guest }}
                        >
                          {getGuestDisplayName(guest.firstName, guest.lastName)}
                        </Link>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {guest.id}
                      </div>
                    </td>
                    <td>{guest.email}</td>
                    <td>{guest.guestTypes.join(", ") || "--"}</td>
                    <td>{guest.validFrom}</td>
                    <td>{guest.validTo}</td>
                    <td className="text-center">
                      {guest.deletedTimestamp ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span aria-label="Guest has been deleted" tabIndex={0}>
                              <Trash2 aria-hidden="true" className="size-4 text-muted-foreground" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Guest has been deleted</TooltipContent>
                        </Tooltip>
                      ) : null}
                    </td>
                  </tr>
                ))}
            </tbody>
          </>
        ) : null}
      </AdminTable>
      <GuestOnboardingModal
        open={onboardModalOpen}
        onClose={() => setOnboardModalOpen(false)}
      />
    </main>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export enum GuestType {
  Resident = "Resident",
  Staff = "Staff",
}

export interface GuestResponse {
  deletedTimestamp: string | null;
  email: string | null;
  firstName: string | null;
  id: string;
  lastName: string | null;
  validFrom: string;
  validTo: string;
  guestTypes: GuestType[];
}

async function getGuests(search: string) {
  const url = `${BASE_API_URL}/api/guest/search?search=${search}`;
  const response = await axios.get<GuestResponse[]>(url);

  return response.data;
}

function useGetGuests(search: string) {
  return useQuery({
    queryKey: ["guests", search],
    queryFn: () => getGuests(search),
    enabled: !!search,
  });
}
